import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const draftApi = {
  document: (id) => axios.get(`${API}/drafts/${id}/document`).then((r) => r.data),
  save: (id, body) => axios.put(`${API}/drafts/${id}/document`, body).then((r) => r.data),
  versions: (id) => axios.get(`${API}/drafts/${id}/versions`).then((r) => r.data),
  version: (id, vid) => axios.get(`${API}/drafts/${id}/versions/${vid}`).then((r) => r.data),
  createVersion: (id, body) => axios.post(`${API}/drafts/${id}/versions`, body).then((r) => r.data),
  restore: (id, vid) => axios.post(`${API}/drafts/${id}/versions/${vid}/restore`, { confirm: true }).then((r) => r.data),
  comments: (id) => axios.get(`${API}/drafts/${id}/comments`).then((r) => r.data),
  addComment: (id, body) => axios.post(`${API}/drafts/${id}/comments`, body).then((r) => r.data),
  patchComment: (id, cid, body) => axios.patch(`${API}/drafts/${id}/comments/${cid}`, body).then((r) => r.data),
  transition: (id, body) => axios.post(`${API}/drafts/${id}/transition`, body).then((r) => r.data),
  policy: () => axios.get(`${API}/drafts/policy`).then((r) => r.data),
  setPolicy: (body) => axios.patch(`${API}/drafts/policy`, body).then((r) => r.data),
  links: (id) => axios.get(`${API}/drafts/${id}/links`).then((r) => r.data),
  alerts: (id) => axios.get(`${API}/drafts/${id}/citation-alerts`).then((r) => r.data),
  alertDecision: (id, body) => axios.post(`${API}/drafts/${id}/citation-alerts/decision`, body).then((r) => r.data),
  addLink: (id, body) => axios.post(`${API}/drafts/${id}/links`, body).then((r) => r.data),
  export: async (id, fmt, params = {}) => {
    const q = new URLSearchParams(params).toString();
    const res = await fetch(`${API}/drafts/${id}/export/${fmt}?${q}`, { credentials: "include" });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Export failed");
    const blob = await res.blob();
    const name = (res.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/)?.[1] || `draft.${fmt}`;
    return { blob, name };
  },
  // Durable export: queued job -> poll -> download. Survives tab reloads (job stays in Background Jobs).
  exportQueued: async (id, fmt, params = {}, onStage) => {
    const { data: job } = await axios.post(`${API}/jobs`, { type: "export", payload: { draftId: id, format: fmt, versionId: params.versionId || null, markup: !!params.markup } });
    for (let i = 0; i < 120; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      const { data } = await axios.get(`${API}/jobs/${job.id}`);
      onStage?.(data.progress?.stage || data.status);
      if (data.status === "COMPLETED") {
        const res = await fetch(`${API}/jobs/${job.id}/download`, { credentials: "include" });
        if (!res.ok) throw new Error("Export file could not be fetched");
        return { blob: await res.blob(), name: data.result?.filename || `draft.${fmt}`, jobId: job.id };
      }
      if (data.status === "FAILED" || data.status === "CANCELLED") throw new Error(data.error || "Export failed. Retry from Background Jobs.");
    }
    throw new Error("Export is taking longer than expected — find it under Background Jobs.");
  },
};

export function downloadBlob(blob, name) {
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

// Loads the structured document and autosaves with optimistic revision control.
export function useDraftDocument(draftId) {
  const [state, setState] = useState(null);
  const [saveStatus, setSaveStatus] = useState("Saved");
  const [error, setError] = useState(null);
  const pending = useRef(null);
  const timer = useRef(null);
  const rev = useRef(null);

  const load = useCallback(async () => {
    try { const d = await draftApi.document(draftId); rev.current = d.rev; setState(d); setSaveStatus(d.locked ? "Approved (locked)" : "Saved"); setError(null); }
    catch (e) { setError(e?.response?.data?.detail || "Could not load draft document"); }
  }, [draftId]);
  useEffect(() => { load(); }, [load]);

  const flush = useCallback(async () => {
    if (!pending.current) return;
    const body = pending.current; pending.current = null;
    setSaveStatus("Saving…");
    try {
      const r = await draftApi.save(draftId, { ...body, rev: rev.current });
      rev.current = r.rev;
      setState((s) => ({ ...s, rev: r.rev, hash: r.hash, workingVersion: r.workingVersion, status: r.status, locked: false, updatedAt: r.updatedAt }));
      setSaveStatus(pending.current ? "Saving…" : "Saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409) setSaveStatus("Conflict Detected");
      else if (!navigator.onLine) setSaveStatus("Offline");
      else setSaveStatus("Save Failed");
      setError(typeof d === "object" ? d.message : d || "Save failed");
      pending.current = body;
    }
  }, [draftId]);

  const schedule = useCallback((doc, pageSetup) => {
    pending.current = { doc, pageSetup };
    setSaveStatus((s) => (s === "Conflict Detected" ? s : "Unsaved"));
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 1200);
  }, [flush]);

  useEffect(() => { const on = () => pending.current && flush(); window.addEventListener("online", on); return () => window.removeEventListener("online", on); }, [flush]);
  useEffect(() => {
    const onHide = () => {
      if (!pending.current) return;
      const body = JSON.stringify({ ...pending.current, rev: rev.current });
      fetch(`${API}/drafts/${draftId}/document`, { method: "PUT", keepalive: true, credentials: "include", headers: { "Content-Type": "application/json" }, body });
      pending.current = null;
    };
    window.addEventListener("pagehide", onHide); window.addEventListener("beforeunload", onHide);
    return () => { window.removeEventListener("pagehide", onHide); window.removeEventListener("beforeunload", onHide); };
  }, [draftId]);
  useEffect(() => () => clearTimeout(timer.current), []);

  return { state, setState, saveStatus, error, schedule, flush, reload: load, saveNow: () => { clearTimeout(timer.current); return flush(); } };
}
